/**
 * Scroll-triggered animations for AguaClara Studio.
 * - .scroll-reveal fade-up on enter
 * - .lottie-divider playback on visible
 * - Hero parallax
 * - Section parallax for [data-parallax] elements
 * - Product card float on scroll
 */

(function () {
  'use strict';

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Scroll reveal ---------- */
  function initScrollReveal() {
    const elements = document.querySelectorAll('.scroll-reveal');
    if (!elements.length) return;

    if (prefersReducedMotion) {
      elements.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    elements.forEach((el) => observer.observe(el));
  }

  /* ---------- Lottie dividers ---------- */
  function initLottieDividers() {
    const dividers = document.querySelectorAll('.lottie-divider');
    if (!dividers.length) return;

    if (prefersReducedMotion) {
      dividers.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            const player = entry.target.querySelector('lottie-player');
            if (player && typeof player.play === 'function') player.play();
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    dividers.forEach((el) => observer.observe(el));
  }

  /* ---------- Hero parallax ---------- */
  function initHeroParallax() {
    const hero = document.querySelector('.custom-hero');
    if (!hero || prefersReducedMotion) return;

    const media = hero.querySelector('.custom-hero__media img');
    if (!media) return;

    media.style.transform = 'translateY(0) scale(1.1)';

    function onScroll() {
      const scrollY = window.scrollY;
      const heroHeight = hero.offsetHeight;
      if (scrollY < heroHeight * 1.5) {
        const offset = scrollY * 0.4;
        media.style.transform = `translateY(${offset}px) scale(1.1)`;
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- General parallax for [data-parallax] elements ---------- */
  function initParallax() {
    if (prefersReducedMotion) return;

    const items = document.querySelectorAll('[data-parallax]');
    if (!items.length) return;

    function updateParallax() {
      const viewportHeight = window.innerHeight;
      const scrollY = window.scrollY;

      items.forEach((el) => {
        const speed = parseFloat(el.dataset.parallax) || 0.1;
        const rect = el.getBoundingClientRect();
        const elCenter = rect.top + rect.height / 2;
        const viewCenter = viewportHeight / 2;
        const distance = elCenter - viewCenter;
        const offset = distance * speed * -1;

        el.style.transform = `translateY(${offset}px)`;
      });
    }

    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          updateParallax();
          ticking = false;
        });
      }
    }, { passive: true });

    updateParallax();
  }

  /* ---------- Product card subtle float ---------- */
  function initProductFloat() {
    if (prefersReducedMotion) return;

    const cards = document.querySelectorAll('.floating-product-card__image-wrapper img');
    if (!cards.length) return;

    function updateFloat() {
      const viewportHeight = window.innerHeight;

      cards.forEach((img) => {
        const rect = img.getBoundingClientRect();
        const center = rect.top + rect.height / 2;
        const normalized = (center - viewportHeight / 2) / viewportHeight;
        const offset = normalized * -15;
        img.style.transform = `translateY(${offset}px)`;
      });
    }

    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          updateFloat();
          ticking = false;
        });
      }
    }, { passive: true });
  }

  /* ---------- About section image parallax ---------- */
  function initAboutParallax() {
    if (prefersReducedMotion) return;

    const img = document.querySelector('.about-maru__image-wrapper img');
    if (!img) return;

    function update() {
      const rect = img.parentElement.getBoundingClientRect();
      const viewH = window.innerHeight;
      if (rect.bottom < 0 || rect.top > viewH) return;

      const progress = (rect.top + rect.height / 2 - viewH / 2) / viewH;
      const offset = progress * -30;
      img.style.transform = `translateY(${offset}px) scale(1.05)`;
    }

    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => { update(); ticking = false; });
      }
    }, { passive: true });

    img.style.transform = 'scale(1.05)';
  }

  /* ---------- Init ---------- */
  function init() {
    initScrollReveal();
    initLottieDividers();
    initHeroParallax();
    initParallax();
    initProductFloat();
    initAboutParallax();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
